import React, { useCallback, useEffect, useRef, useState } from 'react';

import { listRatePlanAddons, removeRatePlanAddon, setRatePlanAddon, type RatePlanAddon, type RatePlanDetail } from './api';
import { majorInput, money, parseCount, parseMajor } from './money';

/**
 * Pricing > a version > Add-ons (tracker 44, esg-01).
 *
 * An add-on is a feature sold per covered asset a month on top of a plan (the
 * first is ESG and sustainability, £1). This is its price list: the price, how
 * many months a year costs on an annual plan, the free trial, which plans can
 * buy it, and which Matrix switches it turns on. It is versioned with the rest
 * of the price list: edit a draft, publish it, and "Sync to Stripe" creates the
 * add-on Prices. The Matrix shows "Add-on" on those switches for those plans.
 */
export default function RatePlanAddons({
  plan,
  toast,
}: {
  plan: RatePlanDetail;
  toast: (message: string) => void;
}) {
  const editable = plan.status === 'draft';
  const cur = plan.currency;
  const [addons, setAddons] = useState<RatePlanAddon[] | null>(null);
  // The parent's toast is a new function each render; read it through a ref so
  // the list loads once per version, not on every render.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const load = useCallback(async () => {
    try {
      setAddons(await listRatePlanAddons(plan.id));
    } catch (e) {
      toastRef.current(`Add-ons: ${(e as Error).message}`);
      setAddons([]);
    }
  }, [plan.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (label: string, next: RatePlanAddon) => {
    try {
      await setRatePlanAddon(plan.id, next);
      toast(label);
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
    }
    await load();
  };

  const editField = (a: RatePlanAddon, field: keyof RatePlanAddon, raw: string) => {
    if (field === 'unitMinor') {
      const minor = parseMajor(raw);
      if (minor == null) return toast('Enter a price');
      return save(`${a.name} price saved`, { ...a, unitMinor: minor });
    }
    if (field === 'annualMonths' || field === 'trialDays') {
      const n = parseCount(raw);
      if (n == null || n < 0) return toast('Enter a number');
      return save(`${a.name} saved`, { ...a, [field]: n });
    }
    if (field === 'name') {
      if (!raw.trim()) return toast('An add-on needs a name');
      return save(`${raw.trim()} renamed`, { ...a, name: raw.trim() });
    }
    if (field === 'description') return save(`${a.name} saved`, { ...a, description: raw.trim() || null });
    return undefined;
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>Add-ons</div>
      <p className="sub" style={{ marginBottom: 12 }}>
        Features bought per covered asset a month on top of a plan. The workspace chooses the whole workspace or chosen
        sites, and gets one free trial. Phones never show these prices.
      </p>
      {addons == null ? <p className="muted">Loading…</p> : null}
      {addons && !addons.length ? <p className="muted">No add-ons on this version.</p> : null}
      {(addons ?? []).map((a) => (
        <div key={a.key} style={{ borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 12, marginTop: 12 }}>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <Field label="Name" editable={editable} value={a.name} width={220} onCommit={(v) => editField(a, 'name', v)} />
            <span className="feat-key">{a.key}</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {a.workspaces ?? 0} workspace{a.workspaces === 1 ? '' : 's'} have it on
            </span>
          </div>
          <Field label="What it does" editable={editable} value={a.description ?? ''} width={520} onCommit={(v) => editField(a, 'description', v)} />
          <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
            <Field
              label="Per asset a month"
              editable={editable}
              value={editable ? majorInput(a.unitMinor) : money(a.unitMinor, cur)}
              width={90}
              onCommit={(v) => editField(a, 'unitMinor', v)}
            />
            <Field
              label="Months a year (annual)"
              editable={editable}
              value={String(a.annualMonths)}
              width={60}
              onCommit={(v) => editField(a, 'annualMonths', v)}
            />
            <Field label="Trial days" editable={editable} value={String(a.trialDays)} width={60} onCommit={(v) => editField(a, 'trialDays', v)} />
            <label style={{ fontSize: 12 }}>
              <div className="muted">What the fee counts</div>
              <select
                value={a.countedOn}
                disabled={!editable}
                onChange={(e) => save(`${a.name}: counts ${e.target.value === 'all_assets' ? 'every asset' : 'the chosen sites'}`, { ...a, countedOn: e.target.value })}
              >
                <option value="chosen_sites">Assets on the sites the customer picks (default: whole workspace)</option>
                <option value="all_assets">Every asset in the workspace</option>
              </select>
            </label>
            <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              A year: {money(a.unitMinor * a.annualMonths, cur)} per asset
              {a.annualMonths < 12 ? ` (${12 - a.annualMonths} month${12 - a.annualMonths === 1 ? '' : 's'} free)` : ''}
            </span>
          </div>
          <div style={{ marginTop: 8 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
              Plans that can buy it (also set in Matrix, where each plan is Off, Add-on or Included)
            </div>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              {plan.editions.map((e) => {
                const on = a.editions.includes(e.plan_key);
                return (
                  <label key={e.plan_key} style={{ fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!editable}
                      onChange={() =>
                        save(`${a.name}: ${e.display_name} ${on ? 'removed' : 'added'}`, {
                          ...a,
                          editions: on ? a.editions.filter((k) => k !== e.plan_key) : [...a.editions, e.plan_key],
                        })
                      }
                    />{' '}
                    {e.display_name}
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 12 }} className="muted">
            Turns on: {a.capabilityKeys.map((k) => <span key={k} className="feat-key" style={{ marginRight: 6 }}>{k}</span>)}
          </div>
          {editable ? (
            <div className="row" style={{ gap: 8, marginTop: 8 }}>
              <button
                className="btn"
                style={{ padding: '4px 8px', fontSize: 12 }}
                onClick={() => save(`${a.name} ${a.isActive ? 'hidden' : 'offered'}`, { ...a, isActive: !a.isActive })}
              >
                {a.isActive ? 'Stop offering' : 'Offer again'}
              </button>
              <button
                className="btn"
                style={{ padding: '4px 8px', fontSize: 12 }}
                onClick={async () => {
                  try {
                    await removeRatePlanAddon(plan.id, a.key);
                    toast(`${a.name} removed from this draft`);
                  } catch (e) {
                    toast(`Refused: ${(e as Error).message}`);
                  }
                  await load();
                }}
              >
                Remove from draft
              </button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Field({
  label,
  value,
  editable,
  width,
  onCommit,
}: {
  label: string;
  value: string;
  editable: boolean;
  width: number;
  onCommit: (next: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <label style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, fontSize: 12, marginTop: 4 }}>
      <span className="muted">{label}</span>
      {editable ? (
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => text !== value && onCommit(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setText(value);
          }}
          style={{ width, padding: '6px 8px', fontSize: 13 }}
        />
      ) : (
        <span style={{ fontSize: 13 }}>{value || '—'}</span>
      )}
    </label>
  );
}
